"use client";

import { useEffect, useRef, useState } from "react";

interface UseDeferredLoadOptions {
  rootMargin?: string;
  idleMs?: number;
}

export default function useDeferredLoad({
  rootMargin = "400px 0px",
  idleMs,
}: UseDeferredLoadOptions = {}) {
  const ref = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    if (shouldLoad) return;

    const node = ref.current;
    if (!node || typeof window === "undefined") return;

    const load = () => setShouldLoad(true);
    let observer: IntersectionObserver | null = null;
    let timeoutId: number | null = null;

    if (!("IntersectionObserver" in window)) {
      load();
      return;
    }

    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          load();
          observer?.disconnect();
        }
      },
      { rootMargin },
    );

    observer.observe(node);

    if (idleMs) {
      timeoutId = window.setTimeout(load, idleMs);
    }

    return () => {
      observer?.disconnect();

      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [idleMs, rootMargin, shouldLoad]);

  return { ref, shouldLoad };
}
